/* Search pages and posts by title and full plaintext content. */
(function () {
    const dialog = document.querySelector('[data-warksar-search-dialog]');
    const indexTemplate = document.querySelector('[data-warksar-search-index]');
    if (!dialog || !indexTemplate || typeof dialog.showModal !== 'function') return;

    const input = dialog.querySelector('[data-warksar-search-input]');
    const form = dialog.querySelector('[data-warksar-search-form]');
    const status = dialog.querySelector('[data-warksar-search-status]');
    const results = dialog.querySelector('[data-warksar-search-results]');
    const closeButton = dialog.querySelector('[data-warksar-search-close]');
    let opener = null;

    const normalise = function (value) {
        return String(value || '')
            .normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLocaleLowerCase()
            .replace(/[^\p{L}\p{N}]+/gu, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    };

    const normaliseWithMap = function (value) {
        const source = String(value || '');
        let text = '';
        const starts = [];
        const ends = [];
        let pendingSpace = false;

        let sourceOffset = 0;
        Array.from(source).forEach(function (character) {
            const characterStart = sourceOffset;
            sourceOffset += character.length;
            const folded = character
                .normalize('NFKD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLocaleLowerCase();
            const alphanumeric = folded.match(/[\p{L}\p{N}]/gu)?.join('') || '';

            if (!alphanumeric) {
                if (/\p{M}/u.test(character)) return;
                if (text) pendingSpace = true;
                return;
            }

            if (pendingSpace && text && !text.endsWith(' ')) {
                text += ' ';
                starts.push(characterStart);
                ends.push(characterStart + character.length);
            }
            pendingSpace = false;

            Array.from(alphanumeric).forEach(function (normalisedCharacter) {
                text += normalisedCharacter;
                starts.push(characterStart);
                ends.push(characterStart + character.length);
            });
        });

        return {source, text, starts, ends};
    };

    const findMatch = function (source, query, terms) {
        const mapped = normaliseWithMap(source);
        let position = mapped.text.indexOf(query);
        let matchedText = query;

        if (position < 0) {
            const termMatch = terms
                .map(function (term) {
                    return {term, position: mapped.text.indexOf(term)};
                })
                .filter(function (candidate) {
                    return candidate.position >= 0;
                })
                .sort(function (a, b) {
                    return a.position - b.position;
                })[0];
            if (!termMatch) return null;
            position = termMatch.position;
            matchedText = termMatch.term;
        }

        let end = position + matchedText.length;
        while (position > 0 && mapped.text[position - 1] !== ' ') position -= 1;
        while (end < mapped.text.length && mapped.text[end] !== ' ') end += 1;

        const sourceStart = mapped.starts[position];
        const sourceEnd = mapped.ends[end - 1];
        if (sourceStart == null || sourceEnd == null) return null;

        return {
            start: sourceStart,
            end: sourceEnd,
            text: mapped.source.slice(sourceStart, sourceEnd),
        };
    };

    const findContentMatch = function (contentChunks, query, terms) {
        const findInChunks = function (fallbackTerms) {
            return contentChunks
                .map(function (chunk) {
                    return {chunk, match: findMatch(chunk.text, query, fallbackTerms)};
                })
                .find(function (candidate) {
                    return candidate.match;
                });
        };

        // Prefer an exact phrase wherever it occurs. Otherwise a common word in
        // an earlier block can steal the deep link from a later exact heading.
        return findInChunks([]) || findInChunks(terms);
    };

    const createMatchPhrase = function (source, match) {
        if (!match) return '';

        const blockStart = source.lastIndexOf('\n', match.start - 1) + 1;
        const nextBreak = source.indexOf('\n', match.end);
        const blockEnd = nextBreak >= 0 ? nextBreak : source.length;
        const before = source.slice(blockStart, match.start);
        const after = source.slice(match.end, blockEnd);
        const wordPattern = /[\p{L}\p{N}’'\-]+/gu;
        const beforeWords = Array.from(before.matchAll(wordPattern));
        const afterWords = Array.from(after.matchAll(wordPattern));
        const leadingWords = beforeWords.slice(-2);
        const trailingWords = afterWords.slice(0, 3);

        const start = leadingWords.length
            ? blockStart + leadingWords[0].index
            : match.start;
        const lastTrailingWord = trailingWords.at(-1);
        const end = lastTrailingWord
            ? match.end + lastTrailingWord.index + lastTrailingWord[0].length
            : match.end;

        return source.slice(start, end).replace(/\s+/g, ' ').trim();
    };

    const encodeTextFragment = function (value) {
        return encodeURIComponent(value).replace(/-/g, '%2D');
    };

    const createResultUrl = function (entry, query, terms) {
        const titleMatch = findMatch(entry.title, query, terms);
        const exactTitleMatch = entry.normalisedTitle === query;
        const contentMatch = findContentMatch(entry.contentChunks, query, terms);
        const match = exactTitleMatch ? titleMatch : (contentMatch?.match || titleMatch);
        if (!match) return entry.url;

        const useContent = !exactTitleMatch && Boolean(contentMatch);
        const exactAnchor = useContent ? contentMatch.chunk.anchor : entry.titleAnchor;
        if (exactAnchor) {
            return entry.url.replace(/#.*$/, '') + '#' + encodeURIComponent(exactAnchor);
        }

        const existingDirective = entry.url.indexOf(':~:');
        const baseUrl = existingDirective >= 0
            ? entry.url.slice(0, existingDirective)
            : entry.url;
        const fragmentMarker = baseUrl.includes('#') ? ':~:' : '#:~:';
        const source = useContent ? contentMatch.chunk.text : entry.title;
        const phrase = createMatchPhrase(source, match);
        return baseUrl + fragmentMarker + 'text=' + encodeTextFragment(phrase || match.text);
    };

    const getContentChunks = function (contentElement) {
        if (!contentElement) return [];

        const blockSelector = 'h1,h2,h3,h4,h5,h6,p,li,blockquote,figcaption,td,th';
        const chunks = Array.from(contentElement.querySelectorAll(blockSelector))
            .filter(function (element) {
                return !element.querySelector(blockSelector);
            })
            .map(function (element) {
                return {
                    text: (element.textContent || '').replace(/\s+/g, ' ').trim(),
                    anchor: /^H[1-6]$/.test(element.tagName) ? element.id : '',
                };
            })
            .filter(function (chunk) {
                return chunk.text;
            });

        if (chunks.length) return chunks;
        const fallback = (contentElement.textContent || '').replace(/\s+/g, ' ').trim();
        return fallback ? [{text: fallback, anchor: ''}] : [];
    };

    const entries = Array.from(indexTemplate.content.querySelectorAll('[data-warksar-search-item]'))
        .map(function (item) {
            const title = item.dataset.searchTitle || '';
            const contentElement = item.querySelector('[data-search-content]');
            const contentChunks = getContentChunks(contentElement);
            const content = contentChunks.map(function (chunk) {
                return chunk.text;
            }).join(' ');
            return {
                title,
                type: item.dataset.searchType || 'Content',
                url: item.dataset.searchUrl || '#',
                titleAnchor: item.dataset.searchTitleAnchor || '',
                content: content.replace(/\s+/g, ' ').trim(),
                contentChunks,
                normalisedTitle: normalise(title),
                normalisedContent: normalise(content),
            };
        })
        .filter(function (entry) {
            return entry.title && entry.url;
        });

    const createSnippet = function (entry, query, terms) {
        const contentMatch = findContentMatch(entry.contentChunks, query, terms);
        const source = contentMatch?.chunk.text || entry.content || entry.title;
        const position = contentMatch?.match.start
            ?? findMatch(source, query, terms)?.start
            ?? 0;
        const start = position > 90 ? position - 70 : 0;
        const end = Math.min(source.length, start + 190);
        const prefix = start > 0 ? '…' : '';
        const suffix = end < source.length ? '…' : '';
        return prefix + source.slice(start, end).trim() + suffix;
    };

    const scoreEntry = function (entry, query, terms) {
        if (!terms.every(function (term) {
            return entry.normalisedTitle.includes(term) || entry.normalisedContent.includes(term);
        })) return -1;

        let score = 0;
        if (entry.normalisedTitle === query) score += 1000;
        if (entry.normalisedTitle.startsWith(query)) score += 500;
        if (entry.normalisedTitle.includes(query)) score += 250;
        terms.forEach(function (term) {
            if (entry.normalisedTitle.includes(term)) score += 100;
            if (entry.normalisedContent.includes(term)) score += 10;
        });
        if (entry.type === 'Page') score += 5;
        return score;
    };

    const renderResults = function () {
        const query = normalise(input.value);
        results.replaceChildren();

        if (!query) {
            status.textContent = 'Search page and post titles, headings and body copy.';
            return;
        }

        const terms = query.split(' ').filter(Boolean);
        const matches = entries
            .map(function (entry) {
                return {entry, score: scoreEntry(entry, query, terms)};
            })
            .filter(function (match) {
                return match.score >= 0;
            })
            .sort(function (a, b) {
                return b.score - a.score || a.entry.title.localeCompare(b.entry.title);
            })
            .slice(0, 20);

        status.textContent = matches.length
            ? `${matches.length} ${matches.length === 1 ? 'result' : 'results'} for “${input.value.trim()}”.`
            : `No results for “${input.value.trim()}”.`;

        matches.forEach(function (match) {
            const item = document.createElement('li');
            const link = document.createElement('a');
            const meta = document.createElement('span');
            const title = document.createElement('strong');
            const snippet = document.createElement('span');

            link.className = 'warksar-search-result';
            link.href = createResultUrl(match.entry, query, terms);
            meta.className = 'warksar-search-result-type';
            meta.textContent = match.entry.type;
            title.className = 'warksar-search-result-title';
            title.textContent = match.entry.title;
            snippet.className = 'warksar-search-result-snippet';
            snippet.textContent = createSnippet(match.entry, query, terms);

            link.append(meta, title, snippet);
            item.appendChild(link);
            results.appendChild(item);
        });
    };

    const openSearch = function (trigger) {
        opener = trigger || document.activeElement;

        const navigation = document.querySelector('.gh-navigation.is-open');
        if (navigation) {
            navigation.classList.remove('is-open');
            navigation.querySelector('.gh-burger')?.setAttribute('aria-expanded', 'false');
        }

        dialog.showModal();
        document.documentElement.style.overflowY = 'hidden';
        input.focus();
        input.select();
    };

    const closeSearch = function () {
        if (dialog.open) dialog.close();
    };

    document.addEventListener('click', function (event) {
        const trigger = event.target.closest('[data-warksar-search]');
        if (!trigger) return;
        event.preventDefault();
        openSearch(trigger);
    });

    window.addEventListener('keydown', function (event) {
        if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') {
            event.preventDefault();
            event.stopImmediatePropagation();
            dialog.open ? closeSearch() : openSearch(document.activeElement);
        }
    }, true);

    input.addEventListener('input', renderResults);
    form.addEventListener('submit', function (event) {
        event.preventDefault();
        results.querySelector('a')?.click();
    });
    input.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown') {
            const firstResult = results.querySelector('a');
            if (firstResult) {
                event.preventDefault();
                firstResult.focus();
            }
        }
    });
    results.addEventListener('keydown', function (event) {
        const firstResult = results.querySelector('a');
        if (event.key === 'ArrowUp' && event.target === firstResult) {
            event.preventDefault();
            input.focus();
        }
    });
    results.addEventListener('click', function (event) {
        const link = event.target.closest?.('a');
        const isPrimaryClick = event.button == null || event.button === 0;
        if (!link || !isPrimaryClick || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        // A text fragment can be a same-document navigation. Close the modal
        // without restoring focus so it cannot obscure or scroll away from the
        // browser-owned destination highlight.
        opener = null;
        closeSearch();
    });
    closeButton.addEventListener('click', closeSearch);
    dialog.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeSearch();
        }
    }, true);
    dialog.addEventListener('cancel', function (event) {
        event.preventDefault();
        closeSearch();
    });
    dialog.addEventListener('click', function (event) {
        if (event.target === dialog) closeSearch();
    });
    dialog.addEventListener('close', function () {
        document.documentElement.style.overflowY = '';
        opener?.focus?.();
        opener = null;
    });
})();
